const NEWS_BAR_TAG = 'news-bar';

/**
 * News Bar component that creates a marquee effect
 * @class NewsBar
 * @extends {HTMLElement}
 */
class NewsBar extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.animating = false;
    this.trackWidth = 0;
    this.trackCloned = false;
    this.containerWidth = 0;
    this.originalItemsWidth = 0;
    this.animationFrame = null;
    this.cloneCount = 0;

    // Bind methods to this instance
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.startAnimation = this.startAnimation.bind(this);
    this.onTransitionEnd = this.onTransitionEnd.bind(this);
    this.cloneItems = this.cloneItems.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector('.news-bar__track');

    if (!this.track) return;

    // Setup events for pause on hover
    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);

    // Initialize after DOM is fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.render.bind(this));
    } else {
      this.render();
    }

    // Store the debounced render function as a class property
    this.debouncedRender = this.debounce(this.render.bind(this), 250);

    // Add resize listener to recalculate on window resize
    window.addEventListener('resize', this.debouncedRender);
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this.onMouseEnter);
    this.removeEventListener('mouseleave', this.onMouseLeave);
    this.track?.removeEventListener('transitionend', this.onTransitionEnd);
    window.removeEventListener('resize', this.debouncedRender);
  }

  /**
   * Clones items if needed to ensure continuous scrolling
   */
  cloneItems() {
    if (this.trackCloned) {
      // Remove existing clones if we need to recalculate
      const items = Array.from(this.track.querySelectorAll('.news-bar__item'));
      for (let i = items.length - 1; i >= this.cloneCount; i--) {
        this.track.removeChild(items[i]);
      }
      this.trackCloned = false;
    }

    const items = Array.from(this.track.querySelectorAll('.news-bar__item'));

    // Only clone if there are items
    if (items.length === 0) return;

    // Store the original count before cloning
    this.cloneCount = items.length;

    // Get container width to determine how many clones we need
    this.containerWidth = this.querySelector('.news-bar__container').offsetWidth;

    // Calculate total width of original items including separators
    this.originalItemsWidth = 0;
    items.forEach(item => {
      this.originalItemsWidth += item.offsetWidth;
    });

    // Determine how many sets of clones we need to ensure continuous scrolling
    // We need at least 3 complete sets to ensure smooth looping
    const minSetsNeeded = Math.max(3, Math.ceil((this.containerWidth * 3) / this.originalItemsWidth));

    // Create enough clones to fill the required width
    for (let i = 0; i < minSetsNeeded - 1; i++) {
      items.forEach(item => {
        const clone = item.cloneNode(true);
        this.track.appendChild(clone);
      });
    }

    this.trackCloned = true;
  }

  /**
   * Gets the current transform X position in a cross-browser compatible way
   * @returns {number} Current X position
   */
  getCurrentPosition() {
    const style = window.getComputedStyle(this.track);
    const transform = style.getPropertyValue('transform') ||
                      style.getPropertyValue('-webkit-transform') ||
                      style.getPropertyValue('-moz-transform');

    if (transform === 'none' || !transform) return 0;

    // Matrix values: matrix(a, b, c, d, tx, ty)
    try {
      const values = transform.split('(')[1].split(')')[0].split(',');
      return parseFloat(values[4]) || 0;
    } catch (e) {
      console.error('Error parsing transform:', e);
      return 0;
    }
  }

  /**
   * Starts the animation
   */
  startAnimation() {
    if (!this.track) return;

    // Get animation speed from CSS variable or default to 30s
    const speed = parseFloat(getComputedStyle(this).getPropertyValue('--news-bar-speed')) || 30;

    // Calculate animation duration based on track width
    // The longer the track, the longer the animation should be to maintain consistent speed
    const scaledDuration = speed * (this.originalItemsWidth / 300); // Scale duration based on content length

    // Reset position
    this.track.style.transform = 'translateX(0)';

    // Force reflow
    void this.track.offsetWidth;

    // Start animation - move by the width of original items to create seamless loop
    this.setAttribute('data-animating', 'true');
    this.track.style.transitionDuration = `${scaledDuration}s`;
    this.track.style.transitionProperty = 'transform';
    this.track.style.transitionTimingFunction = 'linear';

    // Move exactly by the width of the original items to ensure perfect looping
    this.track.style.transform = `translateX(-${this.originalItemsWidth}px)`;

    // Clean up any previous event listeners before adding new ones
    this.track.removeEventListener('transitionend', this.onTransitionEnd);
    this.track.addEventListener('transitionend', this.onTransitionEnd);
  }

  /**
   * Handler for transition end event to restart animation
   */
  onTransitionEnd(e) {
    // Only respond to transform transitions
    if (e.propertyName !== 'transform') return;

    this.setAttribute('data-animating', 'false');
    this.track.removeEventListener('transitionend', this.onTransitionEnd);

    // Reset to beginning without animation
    this.track.style.transition = 'none';
    this.track.style.transform = 'translateX(0)';

    // Force reflow
    void this.track.offsetWidth;

    // Restart animation after a brief delay to ensure smooth transition
    cancelAnimationFrame(this.animationFrame);
    this.animationFrame = requestAnimationFrame(() => {
      this.startAnimation();
    });
  }

  /**
   * Pause animation on mouse enter
   */
  onMouseEnter() {
    if (!this.track) return;

    // Pause any ongoing animation frame
    cancelAnimationFrame(this.animationFrame);

    // Mark as paused
    this.setAttribute('data-paused', 'true');

    // Get the current computed position
    const currentX = this.getCurrentPosition();

    // Immediately stop the animation by removing transition and setting current position
    this.track.style.transition = 'none';
    void this.track.offsetWidth; // Force reflow
    this.track.style.transform = `translateX(${currentX}px)`;
  }

  /**
   * Resume animation on mouse leave
   */
  onMouseLeave() {
    if (!this.track || !this.hasAttribute('data-paused')) return;

    this.removeAttribute('data-paused');

    // Get current position
    const currentX = this.getCurrentPosition();

    // Calculate remaining distance and time
    const speed = parseFloat(getComputedStyle(this).getPropertyValue('--news-bar-speed')) || 30;

    // If we've nearly reached the end point, restart from beginning
    if (Math.abs(currentX) >= (this.originalItemsWidth - 10)) {
      this.track.style.transition = 'none';
      this.track.style.transform = 'translateX(0)';
      void this.track.offsetWidth;
      this.startAnimation();
      return;
    }

    // Calculate the progress through the animation (0 to 1)
    const progress = Math.abs(currentX) / this.originalItemsWidth;

    // Calculate scaled duration based on content length
    const fullDuration = speed * (this.originalItemsWidth / 300);

    // Calculate remaining time based on progress
    const remainingTime = fullDuration * (1 - progress);

    // Resume animation to the next checkpoint
    this.track.style.transitionProperty = 'transform';
    this.track.style.transitionDuration = `${remainingTime}s`;
    this.track.style.transitionTimingFunction = 'linear';
    void this.track.offsetWidth; // Force reflow

    this.track.style.transform = `translateX(-${this.originalItemsWidth}px)`;
    this.setAttribute('data-animating', 'true');

    // Clean up any previous event listeners before adding new ones
    this.track.removeEventListener('transitionend', this.onTransitionEnd);
    this.track.addEventListener('transitionend', this.onTransitionEnd);
  }

  /**
   * Utility function to debounce function calls
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} - Debounced function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Calculate the total width of the track with all items
   */
  calculateTrackWidth() {
    const items = this.track.querySelectorAll('.news-bar__item');
    this.trackWidth = 0;

    items.forEach(item => {
      this.trackWidth += item.offsetWidth;
    });

    return this.trackWidth;
  }

  /**
   * Renders the component
   */
  render() {
    if (!this.track) return;

    // Clean up previous animation
    cancelAnimationFrame(this.animationFrame);
    this.track.removeEventListener('transitionend', this.onTransitionEnd);

    // Reset styles
    this.track.style.transition = 'none';
    this.track.style.transform = 'translateX(0)';
    void this.track.offsetWidth; // Force reflow

    // Clone items to ensure continuous scrolling
    this.cloneItems();

    // Calculate track width with all clones
    this.calculateTrackWidth();

    const items = this.track.querySelectorAll('.news-bar__item');
    if (items.length === 0) return;

    // Always animate as we've now ensured we have enough content
    this.startAnimation();
  }
}

if (!customElements.get(NEWS_BAR_TAG)) {
  customElements.define(NEWS_BAR_TAG, NewsBar);
}
